import { Fragment, FunctionComponent, useCallback, useMemo, useState } from "react";
import { Theme, makeStyles, Typography, Menu, Grid, Button, ButtonProps, useTheme } from "@material-ui/core";
import { APP_BAR_HEIGHT } from "../top-bar";
import { IIconProps } from "../../../assets/icons/icon";
import { DARK_TEXT_COLOR } from "../../../theme/theme";
import { KeyboardArrowDown } from "@material-ui/icons";

const TOP_BAR_BUTTON_BORDER_RADIUS = 10;

interface ITopBarSubButtonStyleProps {
  isActive?: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  button: {
    borderRadius: TOP_BAR_BUTTON_BORDER_RADIUS,
    marginLeft: theme.spacing(4),
  },
  menuButton: {
    borderRadius: TOP_BAR_BUTTON_BORDER_RADIUS,
    width: `calc(100% - ${theme.spacing(4)}px)`,
    padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
    margin: `${theme.spacing(1)}px 0px`,
  },
  icon: {
    width: APP_BAR_HEIGHT * 0.3,
    height: APP_BAR_HEIGHT * 0.3,
    marginRight: theme.spacing(1),
  },
  text: {
    marginLeft: theme.spacing(1),
    fontSize: 16,
    color: (props: ITopBarSubButtonStyleProps) =>
      props.isActive ? theme.palette.primary.main : theme.palette.text.primary,
  },
  subButtonText: {
    fontSize: 16,
    fontWeight: 700,
  },
  menu: {
    "& .MuiPaper-root": {
      marginTop: theme.spacing(10),
      borderRadius: 20,
      minWidth: 170,
      fontSize: 16,
      fontWeight: 700,
      color: DARK_TEXT_COLOR,
      padding: theme.spacing(2),
    },
  },
  menuArrowWrapper: {
    color: (props: ITopBarSubButtonStyleProps) =>
      props.isActive ? theme.palette.primary.main : theme.palette.text.primary,
    display: "flex",
  },
  activeSubButton: {
    color: theme.palette.primary.main,
  },
}));

interface TopBarSubButton extends ButtonProps {
  text: string;
  icon?: FunctionComponent<IIconProps>;
  isActive?: boolean;
}

export interface ITopBarButtonProps extends ButtonProps {
  text: string;
  icon?: FunctionComponent<IIconProps>;
  subButtons?: TopBarSubButton[];
  isActive?: boolean;
}

export const TopBarButton: FunctionComponent<ITopBarButtonProps> = (props) => {
  const theme = useTheme();
  const { icon, subButtons, isActive, ...rest } = props;
  const classes = useStyles({ isActive });
  const [menuAnchor, setMenuAnchor] = useState(null);
  const openMenu = useCallback((event) => setMenuAnchor(event.currentTarget), [setMenuAnchor]);
  const closeMenu = useCallback(() => setMenuAnchor(null), [setMenuAnchor]);
  const isMenu = useMemo(() => subButtons && subButtons.length, [subButtons]);
  const menuOpen = useMemo(() => Boolean(menuAnchor), [menuAnchor]);

  const itemColor = useMemo(() => (isActive ? theme.palette.primary.main : theme.palette.text.primary), [
    isActive,
    theme,
  ]);

  const IconComponent = props.icon;
  return (
    <>
      {!isMenu && (
        <Button {...rest} className={classes.button}>
          <Fragment>
            {IconComponent && (
              <IconComponent
                color={isActive ? theme.palette.primary.main : theme.palette.text.primary}
                className={classes.icon}
              />
            )}
            <Typography className={classes.text}>{props.text}</Typography>
          </Fragment>
        </Button>
      )}
      {isMenu && (
        <>
          <Button {...rest} onClick={openMenu} className={classes.button}>
            <Fragment>
              {IconComponent && <IconComponent color={itemColor} className={classes.icon} />}
              <Typography className={classes.text}>{props.text}</Typography>
              <div className={classes.menuArrowWrapper}>
                <KeyboardArrowDown />
              </div>
            </Fragment>
          </Button>
          <Menu
            id="basic-menu"
            anchorEl={menuAnchor}
            anchorOrigin={{ vertical: "top", horizontal: "left" }}
            transformOrigin={{ vertical: "bottom", horizontal: "left" }}
            open={menuOpen}
            onClose={closeMenu}
            aria-labelledby="basic-button"
            className={classes.menu}
          >
            <Grid container direction={"column"} justify={"center"}>
              {subButtons?.map((buttonProps, index) => {
                const { icon, onClick, isActive, ...rest } = buttonProps;
                const IconComponent = buttonProps.icon;
                return (
                  <Grid container item justify={"center"} key={index}>
                    <Button
                      {...rest}
                      className={`${classes.menuButton} ${buttonProps.className}`}
                      key={index}
                      onClick={(e) => {
                        closeMenu();
                        if (onClick) onClick(e);
                      }}
                    >
                      <Grid container justify={IconComponent ? "flex-start" : "center"} alignItems={"center"}>
                        {IconComponent && (
                          <IconComponent
                            color={isActive ? theme.palette.primary.main : theme.palette.text.primary}
                            className={classes.icon}
                          />
                        )}
                        <span className={`${classes.subButtonText} ${isActive ? classes.activeSubButton : ""}`}>
                          {buttonProps.text}
                        </span>
                      </Grid>
                    </Button>
                  </Grid>
                );
              })}
            </Grid>
          </Menu>
        </>
      )}
    </>
  );
};
